import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { makeStyles } from '@mui/styles'
import {
  CircularProgress,
  Box,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import BookmarkIcon from '@mui/icons-material/Bookmark'
import HelpIcon from '@mui/icons-material/Help'
import PowerCurvePanel from '../panels/PowerCurvePanel'
import NoSite from '../components/NoSite'
import PowerCurveChart from '../charts/PowerCurve/PowerCurveChart'
import SelectorRow from '../charts/PowerCurve/SelectorRow'
import { setSelectedUserSetup } from '../redux/actions/userSetups'
import Api from '../api'
import {
  formatRange,
  normalizePowerCurveData,
  extractRanges,
  MODEL_BASE_COLORS,
} from '../charts/PowerCurve/powerCurveUtils'

// Weight-chip coloring: use each model's chart-line color so the weights band
// doubles as a legend. Text color flips to black on light backgrounds (YIQ)
// so pale models (e.g. UKMO100, ICON_EU) stay readable.
const CHIP_FALLBACK_COLOR = 0x368ed8
// Show weights at most 3 decimals, trimming trailing zeros, so long backend
// floats (e.g. 0.486321987) render cleanly as 0.486 while short values stay as-is.
const formatWeight = w =>
  typeof w === 'number' ? String(Math.round(w * 1000) / 1000) : w
const hexNumToCss = n => `#${n.toString(16).padStart(6, '0')}`
const chipTextColor = n => {
  const r = (n >> 16) & 0xff
  const g = (n >> 8) & 0xff
  const b = n & 0xff
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 150 ? 'rgba(0,0,0,0.87)' : '#fff'
}

const useStyles = makeStyles(theme => ({
  centerItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minWidth: 0,
    width: '100%',
    overflowX: 'hidden',
  },
  weightsRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  weightsLabel: {
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.primary.main,
    whiteSpace: 'nowrap',
  },
  chipRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  chipLabel: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
    whiteSpace: 'nowrap',
  },
  tabPanel: {
    paddingTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minWidth: 0,
    width: '100%',
    flexGrow: 1,
  },
  selectors: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0,
    width: '100%',
    overflowX: 'auto',
  },
  // Per-calibration selector groups laid out in a wrapping row, gap between them
  calGroupRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(4),
    paddingTop: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  calGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  calGroupHeader: {
    fontSize: 12,
    fontWeight: 600,
    color: '#368ED8',
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    width: '100%',
    minHeight: 300,
    color: theme.palette.text.secondary,
    fontSize: 20,
    textAlign: 'center',
  },
  tooltip: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontSize: theme.typography.fontSize,
  },
  arrow: {
    color: theme.palette.primary.main,
  },
  helpIcon: {
    color: theme.palette.primary.main,
  },
}))

// Tab label with bookmark icon for compare toggle
const ModelTabLabel = ({ name, inCompare, onToggle }) => {
  const classes = useStyles()
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <span style={{ fontSize: 12 }}>{name}</span>
      <Tooltip
        title={inCompare ? 'Remove from compare' : 'Add to compare'}
        placement="top"
        arrow
        classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
      >
        <IconButton
          size="small"
          onClick={e => {
            e.stopPropagation()
            onToggle()
          }}
          sx={{ p: 0, ml: 0.25 }}
        >
          {inCompare ? (
            <BookmarkIcon sx={{ fontSize: 13, color: '#368ED8' }} />
          ) : (
            <BookmarkBorderIcon sx={{ fontSize: 13, color: '#bdbdbd' }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  )
}

// Not every setup/plant has power curves. The API answers such a request with
// 404 {"error":"No power curves found for the given plant"} — an empty state,
// not a failure, so it gets its own message instead of the generic fetch error.
const noCurvesMessage = plant => `No power curves available for plant ${plant}.`

const buildCalibrationOptions = calibrations => {
  const sorted = [...calibrations].sort().reverse()
  return sorted.map((date, i) => ({
    value: date,
    label: i === 0 ? `latest (${date})` : date,
  }))
}

// Per-type cascading dimensions for the model tabs, in cascade order under
// calibration: each entry cascades off the ones before it. `prefix` maps to the
// curve fields `${prefix}_start` / `${prefix}_end`.
//
// List every possible selector here; only the ones actually present in a given
// calibration's curves are rendered (active-dimension detection). To change the
// cascade order, reorder the array. To add a selector, add a line.
const MODEL_DIMS = {
  wind: [
    { prefix: 'expression', label: 'Expression' },
    { prefix: 'wind_direction', label: 'Direction' },
    { prefix: 'date', label: 'Date' },
    { prefix: 'time', label: 'Time' },
  ],
  solar: [
    { prefix: 'expression', label: 'Expression' },
    { prefix: 'cloud_cover', label: 'Cloud Cover' },
    { prefix: 'date', label: 'Date' },
    { prefix: 'time', label: 'Time' },
  ],
}

// Does a curve fall in the {start, end} range encoded in a selector value?
const matchRange = (curve, prefix, jsonVal) => {
  const r = JSON.parse(jsonVal)
  const start = `${prefix}_start` in curve ? curve[`${prefix}_start`] : null
  const end = `${prefix}_end` in curve ? curve[`${prefix}_end`] : null
  return start === r.start && end === r.end
}

const PowerCurvePage = props => {
  const classes = useStyles(props)
  const {
    container,
    selectedSetup,
    userPowerSetups,
    setSelectedUserSetup,
    selectedPlant,
    selectedDateRange,
    unit,
    timezone,
  } = props

  const validatedSetup = userPowerSetups.includes(selectedSetup)
    ? selectedSetup
    : null

  const [loading, setLoading] = useState(false)
  const [chartData, setChartData] = useState(null)
  const [trackDisableStatus, setTrackDisableStatus] = useState(false)
  const [fetchError, setFetchError] = useState('')

  const [activeTab, setActiveTab] = useState(0)
  const [compareList, setCompareList] = useState([])
  // Average tab calibration multi-select (top-level calibrations across models)
  const [averageCals, setAverageCals] = useState([])
  // {
  //   [modelName]: {
  //     calibrations: string[],          // selected calibration dates (multi)
  //     average: boolean,                // tab-level average toggle
  //     byCal: { [cal]: { primary: string[], secondary: string[] } },
  //   }
  // }
  // primary/secondary hold selector values (JSON {start,end}); secondary
  // cascades off primary. Dimensions per type come from MODEL_DIMS.
  const [modelSelections, setModelSelections] = useState({})

  const compareTabIndex = chartData ? chartData.models.length + 1 : 0

  const handleRun = async () => {
    setLoading(true)
    setTrackDisableStatus(true)
    setChartData(null)
    setFetchError('')
    setActiveTab(0)
    setCompareList([])
    setModelSelections({})
    setAverageCals([])
    try {
      const response = await Api.PowerCurve.getPowerCurveData(
        validatedSetup,
        selectedPlant,
      )
      if (response.status === 200) {
        const data = normalizePowerCurveData(response.data)
        // A 200 carrying no models is the same story as the 404 below: nothing
        // to plot. Say so rather than render an empty tab strip.
        if (data.models.length === 0) {
          setFetchError(noCurvesMessage(selectedPlant))
          return
        }
        setChartData(data)
        setAverageCals(
          data.calibrations.length > 0 ? [data.calibrations[0]] : [],
        )
        if (data.models.length > 0) {
          const initSelections = {}
          data.models.forEach(m => {
            const latest = m.calibrations.length > 0 ? [m.calibrations[0]] : []
            initSelections[m.name] = {
              calibrations: latest,
              average: true,
              byCal: Object.fromEntries(latest.map(cal => [cal, {}])),
              selectAll: false,
            }
          })
          setModelSelections(initSelections)
        }
      }
    } catch (error) {
      const status = error?.response?.status
      setFetchError(
        status === 404
          ? noCurvesMessage(selectedPlant)
          : 'Error fetching power curve data.',
      )
      console.error('Power curve fetch error:', error)
    } finally {
      setLoading(false)
      setTrackDisableStatus(false)
    }
  }

  const toggleCompare = modelName => {
    setCompareList(prev =>
      prev.includes(modelName)
        ? prev.filter(n => n !== modelName)
        : [...prev, modelName],
    )
  }

  const emptyModelSel = {
    calibrations: [],
    average: true,
    byCal: {},
    selectAll: false,
  }

  // Calibration is the leading selector: changing it syncs the per-calibration
  // groups, keeping existing selections and seeding empty ones for new dates.
  // A manual change drops the "select all" flag so the toggle stays honest.
  const setModelCalibrations = (modelName, vals) => {
    setModelSelections(prev => {
      const cur = prev[modelName] ?? emptyModelSel
      const byCal = {}
      vals.forEach(cal => {
        byCal[cal] = cur.byCal[cal] ?? {}
      })
      return {
        ...prev,
        [modelName]: { ...cur, calibrations: vals, byCal, selectAll: false },
      }
    })
  }

  const setModelAverage = (modelName, on) => {
    setModelSelections(prev => {
      const cur = prev[modelName] ?? emptyModelSel
      return { ...prev, [modelName]: { ...cur, average: on } }
    })
  }

  // Update one calibration group's selection for a dimension (keyed by prefix).
  // Changing a dimension resets every deeper dimension in the cascade.
  const setCalDimension = (modelName, cal, prefix, vals) => {
    const dims = MODEL_DIMS[chartData.type] ?? []
    const changedIdx = dims.findIndex(d => d.prefix === prefix)
    setModelSelections(prev => {
      const cur = prev[modelName] ?? emptyModelSel
      const calSel = { ...(cur.byCal[cal] ?? {}), [prefix]: vals }
      dims.slice(changedIdx + 1).forEach(d => delete calSel[d.prefix])
      return {
        ...prev,
        [modelName]: {
          ...cur,
          byCal: { ...cur.byCal, [cal]: calSel },
          selectAll: false,
        },
      }
    })
  }

  // "Select all" toggle for a model: ON selects every calibration and every
  // cascade value (all curves render); OFF reverts to the initial selection
  // (latest calibration, cascade cleared). The Average toggle is left untouched.
  const toggleModelSelectAll = (modelName, on) => {
    const modelObj = chartData.models.find(m => m.name === modelName)
    if (!modelObj) return
    const dims = MODEL_DIMS[chartData.type] ?? []
    setModelSelections(prev => {
      const cur = prev[modelName] ?? emptyModelSel
      if (on) {
        const byCal = {}
        modelObj.calibrations.forEach(cal => {
          const calCurves = modelObj.curves.filter(c => c.calibration === cal)
          const sel = {}
          dims.forEach(d => {
            const opts = extractRanges(calCurves, d.prefix)
            if (opts.length > 0) sel[d.prefix] = opts.map(o => o.value)
          })
          byCal[cal] = sel
        })
        return {
          ...prev,
          [modelName]: {
            ...cur,
            calibrations: [...modelObj.calibrations],
            byCal,
            selectAll: true,
          },
        }
      }
      const latest =
        modelObj.calibrations.length > 0 ? [modelObj.calibrations[0]] : []
      return {
        ...prev,
        [modelName]: {
          ...cur,
          calibrations: latest,
          byCal: Object.fromEntries(latest.map(cal => [cal, {}])),
          selectAll: false,
        },
      }
    })
  }

  // Reset chart data + tabs when unit/timezone changes, or when any selection
  // the loaded data depends on changes (setup, plant, or date range). Once any
  // of these changes the shown data no longer matches the selection, so it is
  // cleared and RUN must be pressed again.
  useEffect(() => {
    setChartData(null)
    setFetchError('')
    setActiveTab(0)
    setCompareList([])
    setModelSelections({})
    setAverageCals([])
  }, [unit, timezone, validatedSetup, selectedPlant, selectedDateRange])

  if (userPowerSetups.length === 1) {
    setSelectedUserSetup(userPowerSetups[0])
  }

  // Walk the cascade for one calibration's curves.
  // For each dimension (in order), a level is "active" only if the current pool
  // of curves still carries options for it; inactive dimensions are skipped so a
  // selector listed in MODEL_DIMS but absent from the data never blocks anything.
  // Returns:
  //   levels — selectors to render, in order, up to the first one not yet chosen
  //   curves — the fully-narrowed curves, or null while the cascade is incomplete
  const buildCalCascade = (calCurves, dims, calSel) => {
    const levels = []
    let pool = calCurves
    let complete = true
    for (const dim of dims) {
      const options = extractRanges(pool, dim.prefix)
      if (options.length === 0) continue // not present in this pool — skip
      levels.push({ dim, options })
      const selected = calSel[dim.prefix] ?? []
      if (selected.length === 0) {
        complete = false
        break // wait for the user to pick before revealing deeper levels
      }
      pool = pool.filter(c => selected.some(v => matchRange(c, dim.prefix, v)))
    }
    return { levels, curves: complete ? pool : null }
  }

  // Build the visible curves for a model from its per-calibration selections.
  // For each selected calibration: its average curve (when Average is on) plus
  // the dimensional curves left once the whole cascade has been satisfied.
  const getModelCurves = modelName => {
    const data = chartData
    if (!data) return []
    const modelObj = data.models.find(m => m.name === modelName)
    if (!modelObj) return []
    const sel = modelSelections[modelName]
    if (!sel) return []
    const dims = MODEL_DIMS[data.type] ?? []
    const labelsFor = c =>
      dims
        .filter(d => `${d.prefix}_start` in c || `${d.prefix}_end` in c)
        .map(d =>
          formatRange(
            c[`${d.prefix}_start`] ?? null,
            c[`${d.prefix}_end`] ?? null,
          ),
        )

    const result = []
    sel.calibrations.forEach(cal => {
      if (sel.average) {
        const avg = modelObj.averageCurves.find(c => c.calibration === cal)
        if (avg) result.push({ ...avg, _modelName: modelName })
      }

      const calCurves = modelObj.curves.filter(c => c.calibration === cal)
      const { curves: matched } = buildCalCascade(
        calCurves,
        dims,
        sel.byCal[cal] ?? {},
      )
      if (!matched) return
      matched.forEach(c =>
        result.push({ ...c, _modelName: modelName, _dimLabels: labelsFor(c) }),
      )
    })
    return result
  }

  // Average tab: all models' average curves for the selected calibration(s).
  // averageCurves are pre-tagged with _modelName during normalization.
  const getAverageCurves = () => {
    if (!chartData) return []
    return chartData.averageCurves.filter(c =>
      averageCals.includes(c.calibration),
    )
  }

  // ── Tab content renderers ──────────────────────────────────────────────

  const renderAverageTab = () => {
    const calOptions = buildCalibrationOptions(chartData.calibrations)
    const curves = getAverageCurves()
    return (
      <Box className={classes.tabPanel}>
        <Box className={classes.selectors}>
          <SelectorRow
            label="Calibration"
            options={calOptions}
            value={averageCals}
            onChange={setAverageCals}
            multiple
          />
        </Box>
        {averageCals.length === 0 ? (
          <Box className={classes.emptyState}>
            Select a calibration date to view average curves
          </Box>
        ) : curves.length > 0 ? (
          <PowerCurveChart
            chartName="chartAverage"
            type={chartData.type}
            curves={curves}
            height={800}
          />
        ) : (
          <Box className={classes.emptyState}>
            No average curves found for the selected calibration
          </Box>
        )}
      </Box>
    )
  }

  const renderModelTab = modelName => {
    const modelObj = chartData.models.find(m => m.name === modelName)
    if (!modelObj) return null
    const sel = modelSelections[modelName]
    if (!sel) return null
    const dims = MODEL_DIMS[chartData.type] ?? []
    const calOptions = buildCalibrationOptions(modelObj.calibrations)
    const curves = getModelCurves(modelName)

    const emptyMessage =
      sel.calibrations.length === 0
        ? 'Select a calibration date'
        : 'Enable Average, or complete the selectors to view curves'

    return (
      <Box className={classes.tabPanel}>
        {/* Tab-level: calibration (leading) + average toggle */}
        <Box className={classes.selectors}>
          <SelectorRow
            label="Calibration"
            options={calOptions}
            value={sel.calibrations}
            onChange={vals => setModelCalibrations(modelName, vals)}
            multiple
          />
          <SelectorRow
            label="Average"
            options={[{ value: 'average', label: 'Average' }]}
            value={sel.average ? ['average'] : []}
            onChange={vals =>
              setModelAverage(modelName, vals.includes('average'))
            }
            multiple
          />
          <SelectorRow
            label="All"
            options={[{ value: 'all', label: 'Select all' }]}
            value={sel.selectAll ? ['all'] : []}
            onChange={vals =>
              toggleModelSelectAll(modelName, vals.includes('all'))
            }
            multiple
          />
        </Box>

        {/* One group per selected calibration, laid out in a row with a gap */}
        {sel.calibrations.length > 0 && (
          <Box className={classes.calGroupRow}>
            {sel.calibrations.map(cal => {
              const calCurves = modelObj.curves.filter(
                c => c.calibration === cal,
              )
              const calSel = sel.byCal[cal] ?? {}
              const { levels } = buildCalCascade(calCurves, dims, calSel)
              return (
                <Box key={cal} className={classes.calGroup}>
                  <Typography className={classes.calGroupHeader}>
                    {cal}
                  </Typography>
                  {levels.map(({ dim, options }) => (
                    <SelectorRow
                      key={dim.prefix}
                      label={dim.label}
                      options={options}
                      value={calSel[dim.prefix] ?? []}
                      onChange={vals =>
                        setCalDimension(modelName, cal, dim.prefix, vals)
                      }
                      multiple
                    />
                  ))}
                </Box>
              )
            })}
          </Box>
        )}

        {/* Chart / empty state */}
        {curves.length > 0 ? (
          <Box sx={{ mt: 3, width: '100%' }}>
            <PowerCurveChart
              chartName={`chart_${modelName}`}
              type={chartData.type}
              curves={curves}
              height={600}
            />
          </Box>
        ) : (
          <Box className={classes.emptyState}>{emptyMessage}</Box>
        )}
      </Box>
    )
  }

  const renderCompareTab = () => {
    if (compareList.length === 0) {
      return (
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: 'text.secondary',
            gap: 1.5,
          }}
        >
          <Typography
            sx={{ fontSize: 18, fontWeight: 600, color: 'text.primary' }}
          >
            Compare models
          </Typography>
          <Box
            component="ol"
            sx={{
              textAlign: 'left',
              m: 0,
              pl: 2.5,
              fontSize: 15,
              lineHeight: 2,
            }}
          >
            <li>
              Click the bookmark icon{' '}
              <BookmarkBorderIcon
                sx={{ fontSize: 16, mx: 0.5, verticalAlign: 'middle' }}
              />{' '}
              on a model&apos;s tab — no need to open it.
            </li>
            <li>
              The curves selected on that model&apos;s tab are overlaid here.
            </li>
            <li>
              Bookmarked models appear as chips above; remove a model with its
              chip&apos;s ✕ or the bookmark icon again.
            </li>
          </Box>
        </Box>
      )
    }
    const curves = compareList.flatMap(name => getModelCurves(name))
    return (
      <Box className={classes.tabPanel}>
        {curves.length > 0 ? (
          <PowerCurveChart
            chartName="chartCompare"
            type={chartData.type}
            curves={curves}
            height={800}
          />
        ) : (
          <Box className={classes.emptyState}>
            No curves match the current selections
          </Box>
        )}
      </Box>
    )
  }

  // ── Main result panel ──────────────────────────────────────────────────

  const resultPanel = () => {
    if (fetchError) {
      return <NoSite {...props} creating={false} heading={fetchError} body="" />
    }
    if (!chartData) {
      return (
        <NoSite
          {...props}
          creating={false}
          heading={validatedSetup ? '' : 'No setup selected'}
          body={
            validatedSetup
              ? 'Select a plant and press RUN'
              : 'Select a setup and adjust parameters to view the power curve'
          }
        />
      )
    }

    return (
      <Box className={classes.wrapper}>
        {/* Model weights band — applies across all tabs */}
        {chartData.weights?.length > 0 && (
          <Box className={classes.weightsRow}>
            <Typography className={classes.weightsLabel}>WEIGHTS:</Typography>
            {[...chartData.weights]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(w => {
                const base = MODEL_BASE_COLORS[w.name] ?? CHIP_FALLBACK_COLOR
                return (
                  <Chip
                    key={w.name}
                    label={`${w.name}  ${formatWeight(w.weight)}`}
                    size="small"
                    sx={{
                      fontSize: 13,
                      fontWeight: 500,
                      bgcolor: hexNumToCss(base),
                      color: chipTextColor(base),
                      '& .MuiChip-label': { color: 'inherit' },
                    }}
                  />
                )
              })}
          </Box>
        )}

        {/* Compare chip row */}
        {compareList.length > 0 && (
          <Box className={classes.chipRow}>
            <Typography className={classes.chipLabel}>Compare:</Typography>
            {compareList.map(name => (
              <Chip
                key={name}
                label={name}
                size="small"
                onDelete={() => toggleCompare(name)}
                onClick={() => setActiveTab(compareTabIndex)}
                sx={{ cursor: 'pointer', fontSize: 13 }}
              />
            ))}
          </Box>
        )}

        {/* Tab bar — flexGrow:1 + width:0 forces the inner box to exactly the
            available width, so the tab strip can never widen it. This is what
            lets MUI detect overflow and enable horizontal scrolling. */}
        <Box sx={{ display: 'flex', width: '100%' }}>
          <Box sx={{ flexGrow: 1, width: 0 }}>
            <Tabs
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 36 }}
              TabIndicatorProps={{ style: { backgroundColor: '#368ED8' } }}
            >
              <Tab
                label="Average"
                sx={{ fontSize: 12, minHeight: 36, textTransform: 'none' }}
              />
              {chartData.models.map(m => (
                <Tab
                  key={m.name}
                  label={
                    <ModelTabLabel
                      name={m.name}
                      inCompare={compareList.includes(m.name)}
                      onToggle={() => toggleCompare(m.name)}
                    />
                  }
                  sx={{ minHeight: 36, textTransform: 'none' }}
                />
              ))}
              <Tab
                sx={{ fontSize: 12, minHeight: 36, textTransform: 'none' }}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span style={{ fontSize: 12 }}>Compare</span>
                    <Tooltip
                      placement="top"
                      arrow
                      classes={{
                        tooltip: classes.tooltip,
                        arrow: classes.arrow,
                      }}
                      title="Compare models — Click the bookmark icon on a model's tab to add it (no need to open the tab). The curves selected on each model's tab are overlaid here; adjust a model's selection on its tab to change what's compared."
                    >
                      <span>
                        <IconButton
                          style={{ padding: 0 }}
                          aria-label="info"
                          disableTouchRipple
                        >
                          <HelpIcon
                            fontSize="small"
                            className={classes.helpIcon}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                }
              />
            </Tabs>
          </Box>
        </Box>

        {/* Tab content */}
        {activeTab === 0 && renderAverageTab()}
        {chartData.models.map((m, i) =>
          activeTab === i + 1 ? (
            <React.Fragment key={m.name}>
              {renderModelTab(m.name)}
            </React.Fragment>
          ) : null,
        )}
        {activeTab === compareTabIndex && renderCompareTab()}
      </Box>
    )
  }

  return (
    <PowerCurvePanel
      container={container}
      onRun={handleRun}
      disableButton={chartData !== null || loading}
      trackDisableStatus={trackDisableStatus}
    >
      {!loading ? (
        resultPanel()
      ) : (
        <div className={classes.centerItem}>
          <CircularProgress />
        </div>
      )}
    </PowerCurvePanel>
  )
}

const mapStateToProps = state => ({
  selectedSetup: state.userSetups.selectedSetup,
  userPowerSetups: state.userSetups.userPowerSetups,
  selectedPlant: state.powerCurve.selectedPlant,
  selectedDateRange: state.powerCurve.selectedDateRange,
  unit: state.user.unit,
  timezone: state.user.timezone,
})

const mapDispatchToProps = {
  setSelectedUserSetup,
}

export default connect(mapStateToProps, mapDispatchToProps)(PowerCurvePage)
